import { cn } from "@/lib/utils";

import { ChipDelCuadro } from "./ChipDelCuadro";
import type { FiltroDelCuadro, ModoDelTerreno, VistaDelCuadro } from "./cuadroDeConstrucciones";

interface CruceConTerrenoProps {
  modo: Exclude<ModoDelTerreno, "oculto">;
  conteos: VistaDelCuadro["conteos"];
  filtro: FiltroDelCuadro;
  encargado: string | null;
  onElegir: (filtro: FiltroDelCuadro) => void;
}

interface Segmento {
  filtro: FiltroDelCuadro;
  rotulo: string;
  color: string;
}

/**
 * "Cruce con la visita en terreno": una barra que reparte las construcciones
 * entre las que tienen registros del encargado y las que no, con un chip por
 * segmento que además FILTRA la lista.
 *
 * Sin sincronizar hay un solo segmento, "Visita pendiente": decir "sin
 * registros" afirmaría que el encargado fue y no anotó nada.
 *
 * La barra es un atajo con el mouse y los chips son el control accesible, así
 * que los segmentos de la barra no entran al orden de tabulación.
 */
export function CruceConTerreno({ modo, conteos, filtro, encargado, onElegir }: CruceConTerrenoProps) {
  const quien = encargado ?? "El encargado";

  const segmentos: Segmento[] =
    modo === "sincronizado"
      ? [
          { filtro: "con_registros", rotulo: "Con registros de terreno", color: "bg-success-dot/60" },
          { filtro: "sin_registros", rotulo: "Sin registros de terreno", color: "bg-border" },
        ]
      : [{ filtro: "pendiente", rotulo: "Visita pendiente", color: "bg-warning-dot" }];

  // LOS CHIPS SE DIBUJAN SIEMPRE, también con cuenta cero: un control que
  // aparece y desaparece según el dato hace que nadie entienda qué mide. Elegir
  // uno vacío lleva al aviso de «ninguna coincide», que también informa. Lo que
  // sí se omite es el TRAMO de la barra, que con cero no tendría ancho y solo
  // dejaría una rendija — salvo que sea el activo.
  const tramos = segmentos.filter((s) => conteos[s.filtro] > 0 || s.filtro === filtro);

  return (
    <div className="border-divisor-suave flex flex-col gap-2.5 border-t px-5 pt-3.5 pb-3">
      <div className="flex flex-wrap items-baseline gap-2.5">
        <span className="text-ink text-[0.78125rem] font-bold">Cruce con la visita en terreno</span>
        <span className="text-ink-muted text-caption">
          {modo === "sincronizado"
            ? `Qué construcciones tienen fotos, audios o textos de ${quien}. Las notas sobre tipo, año, superficie o clase se ven al abrir cada una.`
            : `${quien} aún no sincroniza la visita.`}
        </span>
      </div>

      <div className="bg-muted flex h-2.5 gap-0.5 overflow-hidden rounded-sm" aria-hidden="true">
        {tramos.map((s) => (
          <span
            key={s.filtro}
            title={`${s.rotulo}: ${conteos[s.filtro]}`}
            onClick={() => onElegir(filtro === s.filtro ? "todas" : s.filtro)}
            style={{ flex: conteos[s.filtro] }}
            className={cn(
              "cursor-pointer",
              s.color,
              filtro !== "todas" && filtro !== s.filtro && "opacity-35",
            )}
          />
        ))}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {segmentos.map((s) => {
          const activo = filtro === s.filtro;

          return (
            <ChipDelCuadro
              key={s.filtro}
              activo={activo}
              rotulo={s.rotulo}
              cantidad={conteos[s.filtro]}
              punto={<span className={cn("size-2 flex-none rounded-full", s.color)} aria-hidden="true" />}
              onClick={() => onElegir(activo ? "todas" : s.filtro)}
            />
          );
        })}
      </div>
    </div>
  );
}
